import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Moon, Sun, Menu, X } from 'lucide-react';
import { 
  LayoutDashboard, ShoppingCart, ChefHat, Users, UtensilsCrossed, 
  MessageSquare, BarChart3, Settings, Building2, Store, CreditCard, Bot, LogOut,
  Sparkles
} from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const DashboardLayout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setSidebarOpen(!mobile); // closed on mobile, open on desktop by default
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar on route change on mobile
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard'), roles: ['super_admin', 'company_admin', 'restaurant_manager', 'staff'] },
    { path: '/companies', icon: Building2, label: t('companies'), roles: ['super_admin'] },
    { path: '/restaurants', icon: Store, label: t('restaurants'), roles: ['super_admin', 'company_admin'] },
    { path: '/onboarding/restaurant', icon: Sparkles, label: 'Restaurant Onboarding', roles: ['company_admin'] },
    { path: '/users', icon: Users, label: t('users'), roles: ['super_admin', 'company_admin'] },
    { path: '/subscriptions', icon: CreditCard, label: 'Subscriptions', roles: ['super_admin', 'company_admin'] },
    { path: '/orders', icon: ShoppingCart, label: t('orders'), roles: ['company_admin', 'restaurant_manager', 'staff'] },
    { path: '/kitchen', icon: ChefHat, label: t('kitchen'), roles: ['restaurant_manager', 'staff'] },
    { path: '/customers', icon: Users, label: t('customers'), roles: ['company_admin', 'restaurant_manager', 'staff'] },
    { path: '/menu', icon: UtensilsCrossed, label: t('menu'), roles: ['company_admin', 'restaurant_manager'] },
    {
  path: '/whatsapp',
  icon: MessageSquare,
  label: t('whatsapp'),
  roles: ['super_admin', 'company_admin', 'restaurant_manager', 'staff']
},
    { path: '/ai-bot', icon: Bot, label: 'AI Bot', roles: ['company_admin', 'restaurant_manager'] },
    { path: '/reports', icon: BarChart3, label: t('reports'), roles: ['super_admin', 'company_admin', 'restaurant_manager'] },
    { path: '/settings', icon: Settings, label: t('settings'), roles: ['super_admin', 'company_admin', 'restaurant_manager'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const roleLabel = 
    user?.role === 'super_admin' ? 'Super Admin' :
    user?.role === 'company_admin' ? 'Restaurant Owner' :
    user?.role === 'restaurant_manager' ? 'Manager' :
    'Kitchen Staff';

  return (
    <div className="flex min-h-screen bg-[#F8FAFC] dark:bg-[#0B0F19]">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 start-0 z-50 bg-[#131B2B] text-white transition-all duration-300 ${
          isMobile 
            ? (sidebarOpen ? 'translate-x-0 rtl:translate-x-0 w-64' : '-translate-x-full rtl:translate-x-full w-64') 
            : (sidebarOpen ? 'w-64' : 'w-20')
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-[#1E293B]">
            {(sidebarOpen || isMobile) && (
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="app-title">
                UniGate
              </h1>
            )}
            {!isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-white hover:bg-white/10"
                data-testid="sidebar-toggle"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            )}
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="text-white hover:bg-white/10"
                data-testid="sidebar-close-mobile"
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4" data-testid="nav-menu">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const showLabel = sidebarOpen || isMobile;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 mb-1 transition-colors ${
                    isActive ? 'bg-white/5 border-s-4 border-[#E57200]' : 'hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {showLabel && <span className="text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>{item.label}</span>}
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-[#1E293B]">
            {(sidebarOpen || isMobile) && (
              <div className="mb-3 text-sm">
                <div className="font-medium truncate" style={{ fontFamily: 'Figtree, sans-serif' }}>{user?.name}</div>
                <div className="text-xs text-gray-400 truncate">{user?.email}</div>
                <div className="mt-1">
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E57200', color: 'white' }}>
                    {roleLabel}
                  </span>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start text-white hover:bg-white/10"
              onClick={logout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5 me-2" />
              {(sidebarOpen || isMobile) && <span style={{ fontFamily: 'Figtree, sans-serif' }}>{t('logout')}</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
        isMobile ? 'ms-0' : (sidebarOpen ? 'ms-64' : 'ms-20')
      }`}>
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white dark:bg-[#131B2B] border-b border-[#E2E8F0] dark:border-[#1E293B] flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-2">
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                data-testid="sidebar-toggle"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 min-h-0 p-4 overflow-hidden md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
